import React, { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Check, Clapperboard, ExternalLink, Instagram, Share2, X, Youtube } from "lucide-react";
import { PHASES, STATUS_META } from "@/data/idevra";
import { sfx } from "@/lib/sound";
import { trophies } from "@/lib/trophies";
import { trackVentureEvent } from "@/lib/ventureStats";

const rise = {
  hidden: { opacity: 0, y: 14 },
  show: (i) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.4, delay: 0.1 + i * 0.08, ease: [0.22, 1, 0.36, 1] },
  }),
};

const SectionLabel = ({ children }) => (
  <div className="f-mono text-[9px] tracking-[0.3em] text-[#0284C7]">{children}</div>
);

export function FilmViewer({ venture, onClose }) {
  const id = venture.film.match(/v=([\w-]+)/)?.[1];

  useEffect(() => {
    trophies.unlock("premiere");
  }, []);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") {
        e.stopImmediatePropagation();
        e.preventDefault();
        sfx.back();
        onClose();
      }
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [onClose]);

  return (
    <motion.div
      className="fixed inset-0 z-[80] flex items-center justify-center p-4 sm:p-10"
      data-testid="film-viewer"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.25 }}
    >
      <button
        type="button"
        aria-label="Close film"
        data-testid="film-viewer-backdrop"
        onClick={() => {
          sfx.back();
          onClose();
        }}
        className="absolute inset-0 bg-[#0F172A]/70 backdrop-blur-md"
      />
      <motion.div
        className="iv-glass relative w-full max-w-4xl overflow-hidden rounded-2xl"
        initial={{ scale: 0.94, y: 20 }}
        animate={{ scale: 1, y: 0 }}
        exit={{ scale: 0.96, y: 12, opacity: 0 }}
        transition={{ type: "spring", stiffness: 300, damping: 28 }}
      >
        <div className="flex items-center justify-between border-b border-white/60 px-5 py-3">
          <span className="f-syncopate text-[10px] tracking-[0.3em] text-[#0F172A]">
            {venture.name.toUpperCase()} — BRAND FILM
          </span>
          <button
            type="button"
            data-testid="film-viewer-close"
            onClick={() => {
              sfx.back();
              onClose();
            }}
            className="flex h-8 w-8 items-center justify-center rounded-full border border-[#CBD5E1] text-[#475569] transition-colors hover:border-[#38BDF8] hover:text-[#0284C7]"
          >
            <X size={13} />
          </button>
        </div>
        <div className="aspect-video w-full bg-black">
          <iframe
            data-testid="film-iframe"
            className="h-full w-full"
            src={`https://www.youtube-nocookie.com/embed/${id}?autoplay=1&rel=0`}
            title={`${venture.name} brand film`}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        </div>
        <div className="f-mono px-5 py-2.5 text-[8px] tracking-[0.25em] text-[#94A3B8]">
          ○ BACK / ESC TO CLOSE · STREAMED FROM YOUTUBE
        </div>
      </motion.div>
    </motion.div>
  );
}

export default function VentureOverlay({ venture, onClose }) {
  const [copied, setCopied] = useState(false);
  const [showFilm, setShowFilm] = useState(false);
  const openedRef = useRef(null);

  useEffect(() => {
    if (openedRef.current === venture.slug) return;
    openedRef.current = venture.slug;
    trackVentureEvent(venture.slug, "open");
  }, [venture.slug]);

  const handleShare = async () => {
    sfx.clank();
    const url = `${window.location.origin}/api/share/${venture.slug}`;
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2400);
    } catch {
      if (navigator.share) navigator.share({ title: venture.name, url }).catch(() => {});
    }
  };

  return (
    <motion.div
      className="absolute inset-0 z-40 flex items-center justify-center p-3 sm:p-8"
      data-testid="venture-overlay"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.25 }}
    >
      <button
        type="button"
        aria-label="Close"
        onClick={onClose}
        className="absolute inset-0 bg-[#F1F5F9]/70 backdrop-blur-md"
        data-testid="venture-overlay-backdrop"
      />
      <motion.div
        className="iv-glass iv-overlay-card relative flex max-h-[92vh] w-full max-w-5xl flex-col overflow-hidden rounded-2xl"
        initial={{ scale: 0.93, y: 26 }}
        animate={{ scale: 1, y: 0 }}
        exit={{ scale: 0.95, y: 16, opacity: 0 }}
        transition={{ type: "spring", stiffness: 280, damping: 26 }}
      >
        {/* Header */}
        <div className="flex items-center justify-between gap-4 border-b border-white/70 px-5 py-4 sm:px-8">
          <div>
            <div className="f-mono text-[9px] tracking-[0.3em] text-[#0284C7]">VENTURE DOSSIER</div>
            <h3 className="f-orbitron mt-1 text-lg font-bold text-[#0F172A] sm:text-2xl">{venture.name}</h3>
            <div className="f-mono mt-0.5 text-[9px] uppercase tracking-[0.2em] text-[#64748B]">
              {venture.sector}
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span className={`iv-status hidden sm:block ${STATUS_META[venture.status].cls} !text-[10px]`}>
              {STATUS_META[venture.status].label}
            </span>
            <button
              type="button"
              data-testid="venture-overlay-close"
              onClick={onClose}
              className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-[#CBD5E1] text-[#475569] transition-colors hover:border-[#38BDF8] hover:text-[#0284C7]"
            >
              <X size={15} />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="iv-panel-scroll grid flex-1 gap-6 overflow-y-auto p-5 sm:p-8 md:grid-cols-[280px_1fr]">
          <div className="flex gap-3 md:flex-col" data-testid="dossier-gallery">
            <img
              src={venture.cover}
              alt={venture.name}
              className="h-40 w-1/2 rounded-xl object-cover md:h-auto md:w-full md:aspect-[2/3]"
            />
            <img
              src={venture.gallery}
              alt={`${venture.name} facility`}
              data-testid="dossier-gallery-img"
              className="h-40 w-1/2 rounded-xl object-cover md:h-auto md:w-full md:aspect-video"
            />
          </div>

          <div className="flex flex-col gap-6">
            <motion.p
              variants={rise}
              initial="hidden"
              animate="show"
              custom={0}
              className="text-sm leading-relaxed text-[#475569]"
            >
              {venture.summary}
            </motion.p>

            {venture.capabilities && (
              <motion.div
                variants={rise}
                initial="hidden"
                animate="show"
                custom={0.5}
                className="flex flex-wrap gap-2"
                data-testid="dossier-capabilities"
              >
                {venture.capabilities.map((c) => (
                  <span key={c} className="iv-chip !px-3 !py-1.5 !text-[9px]">{c}</span>
                ))}
              </motion.div>
            )}

            <motion.div variants={rise} initial="hidden" animate="show" custom={1} data-testid="dossier-stats">
              <SectionLabel>— ATTRIBUTES</SectionLabel>
              <div className="mt-3 flex flex-col gap-2.5">
                {venture.stats.map((s, i) => (
                  <div key={s.label} className="flex items-center gap-3">
                    <span className="f-mono w-28 shrink-0 text-[9px] tracking-[0.18em] text-[#64748B]">
                      {s.label}
                    </span>
                    <div className="iv-stat-track flex-1">
                      <motion.div
                        className="iv-stat-fill"
                        initial={{ width: 0 }}
                        animate={{ width: `${s.value}%` }}
                        transition={{ duration: 0.9, delay: 0.3 + i * 0.12, ease: "easeOut" }}
                      />
                    </div>
                    <span className="f-orbitron w-8 text-right text-xs font-bold text-[#0284C7]">{s.value}</span>
                  </div>
                ))}
              </div>
            </motion.div>

            <motion.div variants={rise} initial="hidden" animate="show" custom={2} data-testid="dossier-phases">
              <SectionLabel>— DEVELOPMENT PHASE</SectionLabel>
              <div className="mt-4 flex items-start">
                {PHASES.map((p, i) => (
                  <React.Fragment key={p}>
                    {i > 0 && <span className={`iv-phase-line ${i <= venture.phase ? "done" : ""}`} />}
                    <div className="flex flex-col items-center gap-1.5">
                      <span
                        className={`iv-phase-dot ${
                          i < venture.phase ? "done" : i === venture.phase ? "current" : ""
                        }`}
                      />
                      <span
                        className={`f-mono text-[7px] tracking-[0.15em] sm:text-[8px] ${
                          i <= venture.phase ? "text-[#0F172A]" : "text-[#94A3B8]"
                        }`}
                      >
                        {p}
                      </span>
                    </div>
                  </React.Fragment>
                ))}
              </div>
            </motion.div>

            <motion.div variants={rise} initial="hidden" animate="show" custom={3} data-testid="dossier-milestones">
              <SectionLabel>— MILESTONE LOG</SectionLabel>
              <ul className="mt-3 flex flex-col gap-1.5">
                {venture.milestones.map((m) => (
                  <li key={m.label} className="flex items-start gap-2 f-mono text-[10px] tracking-[0.1em]">
                    <span className={m.done ? "text-[#0284C7]" : "text-[#CBD5E1]"}>{m.done ? "▣" : "▢"}</span>
                    <span className={m.done ? "text-[#334155]" : "text-[#94A3B8]"}>{m.label}</span>
                  </li>
                ))}
              </ul>
            </motion.div>

            <motion.div
              variants={rise}
              initial="hidden"
              animate="show"
              custom={4}
              className="mt-auto flex items-center gap-4 pt-1"
            >
              {venture.url ? (
                <a
                  href={venture.url}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="venture-launch-btn"
                  onClick={() => {
                    sfx.clank();
                    trophies.unlock("deep-dive");
                    trackVentureEvent(venture.slug, "launch");
                  }}
                  className="f-syncopate inline-flex items-center gap-2 rounded-full bg-[#0284C7] px-6 py-3 text-[10px] tracking-[0.25em] text-white shadow-[0_0_24px_rgba(56,189,248,0.5)] transition-colors hover:bg-[#0369A1]"
                >
                  LAUNCH <ExternalLink size={12} />
                </a>
              ) : (
                <span className="f-mono text-[10px] tracking-[0.25em] text-[#94A3B8]">
                  IN DEVELOPMENT — COMING ONLINE
                </span>
              )}
              <button
                type="button"
                data-testid="venture-share-btn"
                onClick={handleShare}
                className={`f-syncopate inline-flex items-center gap-2 rounded-full border px-5 py-3 text-[10px] tracking-[0.25em] transition-colors ${
                  copied
                    ? "border-[#0284C7] text-[#0284C7]"
                    : "border-[#CBD5E1] text-[#475569] hover:border-[#38BDF8] hover:text-[#0284C7]"
                }`}
              >
                {copied ? (
                  <>
                    COPIED <Check size={12} />
                  </>
                ) : (
                  <>
                    SHARE <Share2 size={12} />
                  </>
                )}
              </button>
              {venture.youtube && (
                <a
                  href={venture.youtube}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="venture-youtube-btn"
                  title={`${venture.name} on YouTube`}
                  onClick={() => sfx.clank()}
                  className="flex h-10 w-10 items-center justify-center rounded-full border border-[#CBD5E1] text-[#475569] transition-colors hover:border-[#38BDF8] hover:text-[#0284C7]"
                >
                  <Youtube size={15} />
                </a>
              )}
              {venture.instagram && (
                <a
                  href={venture.instagram}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="venture-instagram-btn"
                  title={`${venture.name} on Instagram`}
                  onClick={() => sfx.clank()}
                  className="flex h-10 w-10 items-center justify-center rounded-full border border-[#CBD5E1] text-[#475569] transition-colors hover:border-[#38BDF8] hover:text-[#0284C7]"
                >
                  <Instagram size={15} />
                </a>
              )}
              {venture.film && (
                <button
                  type="button"
                  data-testid="venture-film-btn"
                  title="Watch the brand film"
                  onClick={() => {
                    sfx.clank();
                    setShowFilm(true);
                  }}
                  className="flex h-10 w-10 items-center justify-center rounded-full border border-[#CBD5E1] text-[#475569] transition-colors hover:border-[#38BDF8] hover:text-[#0284C7]"
                >
                  <Clapperboard size={15} />
                </button>
              )}
              <span className="f-mono hidden text-[9px] tracking-[0.2em] text-[#CBD5E1] sm:block">○ BACK / ESC</span>
            </motion.div>
          </div>
        </div>
      </motion.div>
      <AnimatePresence>
        {showFilm && <FilmViewer venture={venture} onClose={() => setShowFilm(false)} />}
      </AnimatePresence>
    </motion.div>
  );
}
